'use client';

import { useCookies } from 'react-cookie';
import { PropsWithChildren, createContext, useCallback, useRef } from 'react';
import useModal from '@/utils/use-modal';
import { COOKIE_CONSENT_KEY, IS_IRAN_KEY } from './constants';
import { CallbackType, ConsentCookieKeys, ConsentCookieValues, CookieConsentContextValueType, CookieConsentValues } from './type';
const CookieConsentContext = createContext<CookieConsentContextValueType>({
  fireAllCallbacks: () => null,
  addCallbackToQueue: () => null,
  closeConsentModal: () => null,
  changeCookieConsentHander: () => null,
  isConsentModalOpen: false
});
const CookieConsentContextProvider = ({
  children
}: PropsWithChildren) => {
  const calbacksQueueRef = useRef<CallbackType[]>([]);
  const [cookies, setCookie] = useCookies<ConsentCookieKeys, ConsentCookieValues>([COOKIE_CONSENT_KEY, IS_IRAN_KEY]);
  const isForeign = cookies[IS_IRAN_KEY] === false;
  const hasUserAlreadyConsent = cookies[COOKIE_CONSENT_KEY] !== undefined;
  const mustShowConsentModal = isForeign && !hasUserAlreadyConsent;
  const {
    handleCloseModal: closeConsentModal,
    isModalOpen: isConsentModalOpen
  } = useModal(mustShowConsentModal);
  const addCallbackToQueue = useCallback((newCallBack: () => void) => {
    if (!isConsentModalOpen) {
      newCallBack();
      return;
    }
    const curentList = calbacksQueueRef.current;
    curentList.push(newCallBack);
    calbacksQueueRef.current = curentList;
  }, [isConsentModalOpen]);
  const fireAllCallbacks = useCallback(() => {
    calbacksQueueRef.current.forEach(cb => cb());
    calbacksQueueRef.current = [];
  }, []);
  const changeCookieConsentHander = useCallback((value: CookieConsentValues) => {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30);
    setCookie(COOKIE_CONSENT_KEY, value, {
      expires: expirationDate,
      path: '/'
    });
  }, [setCookie]);
  return <CookieConsentContext.Provider value={{
    fireAllCallbacks,
    addCallbackToQueue,
    closeConsentModal,
    isConsentModalOpen,
    changeCookieConsentHander
  }} data-sentry-element="unknown" data-sentry-component="CookieConsentContextProvider" data-sentry-source-file="cookie-consent-context.tsx">
            {children}
        </CookieConsentContext.Provider>;
};
export { CookieConsentContext };
export default CookieConsentContextProvider;